import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcessClientOrigin,
  CreationOriginType,
  ProcessTaskExecutionOriginType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ItemListingQuery} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import APIUtil from 'util/APIUtil';

type SidepanelProcessTask = ExtractByTypename<ItemListingQuery['processTasks'][number], 'ProcessTaskNode'>;

type SidepanelProcessOutput = ExtractByTypename<
  ItemListingQuery['processOutputs'][number],
  'ProcessOutputNode'
>;

type SidepanelProcess = ExtractByTypename<ItemListingQuery['processes'][number], 'ProcessNode'>;

export type SidepanelProcessItem = SidepanelProcess | SidepanelProcessTask | SidepanelProcessOutput;

export function sidepanelProcessItemIsProcess(item: SidepanelProcessItem): item is SidepanelProcess {
  return APIUtil.isProcess(item.id);
}

export function sidepanelProcessItemIsProcessTask(item: SidepanelProcessItem): item is SidepanelProcessTask {
  return APIUtil.isProcessTask(item.id);
}

export function sidepanelProcessItemIsProcessOutput(
  item: SidepanelProcessItem
): item is SidepanelProcessOutput {
  return APIUtil.isProcessOutput(item.id);
}

export function doSelectedItemsExceedLimit(selectedItems: Array<SidepanelProcessItem>): {
  isExceedingLimit: boolean;
  isExceedingLimitMessage: string | null;
} {
  const isExceedingLimit = selectedItems.length > BenchlingConfig.WORKFLOWS_MAX_ITEMS_PER_BULK_ACTION;

  return {
    isExceedingLimit,
    isExceedingLimitMessage: isExceedingLimit
      ? `Only ${BenchlingConfig.WORKFLOWS_MAX_ITEMS_PER_BULK_ACTION} items can be selected at a time`
      : null,
  };
}
export function taskHasExecutionEntry(item: SidepanelProcessTask): item is SidepanelProcessTask & {
  executionOrigin: {executionType: ProcessTaskExecutionOriginType.ENTRY; executionEntry: {id: string}};
} {
  return item.executionOrigin?.executionType === ProcessTaskExecutionOriginType.ENTRY;
}

export function outputHasCreationEntry(item: SidepanelProcessOutput): item is SidepanelProcessOutput & {
  creationOrigin: {originType: CreationOriginType.ENTRY; origin: {id: string}};
} {
  return item.creationOrigin.originType === CreationOriginType.ENTRY;
}

export function raiseInvalidProcessItemError(): void {
  assert(false, 'Expected process item to be Process, ProcessTask, or ProcessOutput');
}

export function getWorkspaceItemIdFromProcessItem(item: SidepanelProcessItem): string {
  return sidepanelProcessItemIsProcessOutput(item) ? item.task.id : item.id;
}

export const SUPPORTED_DASHBOARD_TYPES_BY_PROCESS_CLIENT_ORIGIN = {
  [ProcessClientOrigin.PROCESSES]: [
    ModelTypeConstants.PROCESS,
    ModelTypeConstants.PROCESS_TASK,
    ModelTypeConstants.PROCESS_OUTPUT,
  ],
  [ProcessClientOrigin.RECIPES]: [
    ModelTypeConstants.RECIPE_RUN_TASK,
    ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK,
    ModelTypeConstants.RECIPE_STEP_TASK,
  ],
  [ProcessClientOrigin.ASSAYS]: [
    ModelTypeConstants.ASSAY_RUN_TASK,
    ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK,
    ModelTypeConstants.ASSAY_STEP_TASK,
  ],
};

// Somewhat confusingly the process dashboard can support multiple
// types of client origins; one of these client origins is also called processes.
// The client origin denotes which "product" within the app the model comes from.
// So if we are displaying the process dashboard with a process client origin, we're
// showing tasks that were created by the processes product itself. Tasks can also be created
// by other products (e.g. recipes) which use processes as a platform.
export function areProcessDashboardModelTypesSupported(
  clientOrigin: ProcessClientOrigin,
  modelTypes: Array<string>
): boolean {
  return (
    SUPPORTED_DASHBOARD_TYPES_BY_PROCESS_CLIENT_ORIGIN[clientOrigin] &&
    modelTypes.every((modelType) =>
      (SUPPORTED_DASHBOARD_TYPES_BY_PROCESS_CLIENT_ORIGIN[clientOrigin] as Array<string>).includes(modelType)
    )
  );
}

export function isDashboardTypeAssignable(dashboardType: ModelTypeConstants): boolean {
  // these are actually flowchart-type tasks that we broke out into their own dashboard types
  // for UI presentation purposes. Flowchart tasks are not assignable.
  if (
    dashboardType === ModelTypeConstants.RECIPE_RUN_TASK ||
    dashboardType === ModelTypeConstants.ASSAY_RUN_TASK
  ) {
    return false;
  }

  return getModelTypeFromDashboardType(dashboardType) === ModelTypeConstants.PROCESS_TASK;
}

export const _DASHBOARD_TYPE_TO_MODEL_TYPE_OVERRIDE: {[key in ModelTypeConstants]?: ModelTypeConstants} = {
  [ModelTypeConstants.RECIPE_PROCESS]: ModelTypeConstants.PROCESS,
  [ModelTypeConstants.RECIPE_PROCESS_OUTPUT]: ModelTypeConstants.PROCESS_OUTPUT,
  [ModelTypeConstants.RECIPE_RUN_TASK]: ModelTypeConstants.PROCESS_TASK,
  [ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK]: ModelTypeConstants.PROCESS_TASK,
  [ModelTypeConstants.RECIPE_STEP_TASK]: ModelTypeConstants.PROCESS_TASK,
  [ModelTypeConstants.ASSAY_PROCESS]: ModelTypeConstants.PROCESS,
  [ModelTypeConstants.ASSAY_PROCESS_OUTPUT]: ModelTypeConstants.PROCESS_OUTPUT,
  [ModelTypeConstants.ASSAY_RUN_TASK]: ModelTypeConstants.PROCESS_TASK,
  [ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK]: ModelTypeConstants.PROCESS_TASK,
  [ModelTypeConstants.ASSAY_STEP_TASK]: ModelTypeConstants.PROCESS_TASK,
};

export function getModelTypeFromDashboardType(dashboardType: ModelTypeConstants): ModelTypeConstants {
  return _DASHBOARD_TYPE_TO_MODEL_TYPE_OVERRIDE[dashboardType] ?? dashboardType;
}
